import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_POSTING_MODE, featuresForRoute, missingForRoute, type BranchDto, type FeatureKey, type MeDto, type PostingDoc, type PostingMode, type TreasuryDto, type WarehouseDto } from "@delta/shared";
import { api, setUnauthenticatedHandler } from "./api";

interface SessionCtx {
  me: MeDto | null;
  loading: boolean;
  branches: BranchDto[];
  warehouses: WarehouseDto[];
  treasuries: TreasuryDto[];
  /** Top-bar branch filter: "all" (administrators only) or a branch id. */
  branch: string;
  setBranch(id: string): void;
  signIn(phone: string, password: string, remember: boolean): Promise<MeDto>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
  /** UI-only checks used to hide menus and disable buttons; the server enforces the same keys itself. */
  can(key: string): boolean;
  /** The business owner: the only user who posts invoices to the accounts (the server enforces it, owner_only). */
  owner: boolean;
  /** Whether the business has an owner. Without one nobody posts (owner_unassigned) until an operator assigns one. */
  ownerAssigned: boolean;
  /** Posting policies (migration 034): whether this user may post the type (the owner, or a right given), and its mode. */
  canPost(doc: PostingDoc): boolean;
  postMode(doc: PostingDoc): PostingMode;
  /**
   * What the user lacks for a screen: permission keys, and "feature:<key>" when the business's package leaves the
   * screen's module out (migration 043). A package gives no permission and no permission unlocks a missing feature.
   */
  missingFor(path: string): string[];
  /** Whether the business's package includes the feature (always true for organizations Delta provisioned). */
  hasFeature(f: FeatureKey): boolean;
  /** No trial or subscription in force (migration 044): the software is closed; only the owner's package screen remains. */
  closed: boolean;
}

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState<BranchDto[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [treasuries, setTreasuries] = useState<TreasuryDto[]>([]);
  const [branch, setBranch] = useState("all");

  const adopt = useCallback(async (m: MeDto | null) => {
    setMe(m);
    if (!m) { setBranches([]); setWarehouses([]); setTreasuries([]); return; }
    setBranch(m.user.administrator ? "all" : m.scope.branchId ?? "all");
    // The server returns only what the user may use; nothing here widens it.
    const [b, w, tr] = await Promise.all([
      api<BranchDto[]>("GET", "/branches").catch(() => []),
      api<WarehouseDto[]>("GET", "/warehouses").catch(() => []),
      api<TreasuryDto[]>("GET", "/treasuries").catch(() => [])
    ]);
    setBranches(b); setWarehouses(w); setTreasuries(tr);
  }, []);

  const refresh = useCallback(async () => {
    try { await adopt(await api<MeDto>("GET", "/auth/me")); } catch { await adopt(null); }
  }, [adopt]);

  useEffect(() => {
    setUnauthenticatedHandler(() => setMe(null));
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const value = useMemo<SessionCtx>(() => ({
    me, loading, branches, warehouses, treasuries, branch,
    setBranch: (id) => { if (me?.user.administrator || id === me?.scope.branchId) setBranch(id); },
    signIn: async (phone, password, remember) => {
      const m = await api<MeDto>("POST", "/auth/login", { phone, password, remember });
      await adopt(m);
      return m;
    },
    signOut: async () => {
      try { await api("POST", "/auth/logout"); } finally { await adopt(null); }
    },
    refresh,
    owner: !!me?.user.owner,
    ownerAssigned: !!me?.organization.ownerAssigned,
    canPost: (d) => !!me?.posting?.post.includes(d),
    postMode: (d) => me?.posting?.modes[d] ?? DEFAULT_POSTING_MODE[d],
    can: (k) => !!me && (me.user.administrator || me.permissions.includes(k)),
    hasFeature: (f) => !me?.package || me.package.features.includes(f),
    closed: !!me?.package && me.package.access !== "open",
    missingFor: (path) => {
      if (!me) return [];
      const off = me.package ? featuresForRoute(path).filter((f) => !me.package!.features.includes(f)) : [];
      const keys = missingForRoute(path, { administrator: me.user.administrator, permissions: me.permissions });
      return [...off.map((f) => `feature:${f}`), ...keys];
    }
  }), [me, loading, branches, warehouses, treasuries, branch, adopt, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSession outside SessionProvider");
  return c;
}
